import {
  BottomSheet,
  BottomSheetIllustrationContent,
  Input,
  Text,
  useModalState,
} from "@clipboard-health/ui-components";
import { getSafeAreaPaddings, getTheme } from "@clipboard-health/ui-theme";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormLabel, RadioGroup, Stack, ThemeProvider } from "@mui/material";
import { RadioField } from "@src/appV2/lib/FormFields/RadioField/RadioField";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { isEmpty, toNumber } from "lodash";
import { useEffect, useRef } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { Redirect, useHistory } from "react-router-dom";
import { z } from "zod";

import { TabRouterPath } from "../../routing/constant/tabRoute";
import { SAVINGS_GOAL_OPTIONS, SAVINGS_GOAL_SELECT_DISPLAY } from "../constants";
import { useWorkerSavingsData } from "../useWorkerSavingsData";
import { handleNumberInputKeyDown, roundToNearestCent } from "../utils";

interface FormData {
  savingsGoal: string;
  savingsReason: string;
  savingsAmount: string;
}

export function SavingGoalsConfigurationPage() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const { workerSavingsData, setWorkerSavingsData } = useWorkerSavingsData();
  const onboardingModalState = useModalState();
  const hasShownOnboarding = useRef(false);

  const form = useForm({
    defaultValues: {
      savingsGoal: workerSavingsData.savingsOnboardingCompleted
        ? workerSavingsData.savingsGoal.toString()
        : "",
      savingsReason: workerSavingsData.savingsOnboardingCompleted
        ? workerSavingsData.savingsReason
        : "",
      savingsAmount: workerSavingsData.savingsOnboardingCompleted
        ? workerSavingsData.savingsAmount.toString()
        : "",
    },
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(
      z
        .object({
          savingsGoal: z.string().min(1, "Please enter an amount"),
          savingsReason: z.string(),
          savingsAmount: z.string().min(0, "Please enter your current savings"),
        })
        .refine(
          (data) => {
            return !Number.isNaN(Number(data.savingsGoal));
          },
          {
            message: "Savings goal must be a number",
            path: ["savingsGoal"],
          }
        )
        .refine(
          (data) => {
            return Number(data.savingsGoal) > 0;
          },
          {
            message: "Savings goal must be greater than 0",
            path: ["savingsGoal"],
          }
        )
        .refine(
          (data) => {
            return !Number.isNaN(Number(data.savingsAmount));
          },
          {
            message: "Current savings must be a number",
            path: ["savingsAmount"],
          }
        )
    ),
  });

  useEffect(() => {
    if (!workerSavingsData.savingsOnboardingCompleted && !hasShownOnboarding.current) {
      onboardingModalState.openModal();
      hasShownOnboarding.current = true;
    }
  }, [workerSavingsData.savingsOnboardingCompleted, onboardingModalState]);

  const handleOnboardingClose = () => {
    onboardingModalState.closeModal();
  };

  const handleGoalBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const rawValue = toNumber(event.target.value);
    if (Number.isNaN(rawValue)) {
      return;
    }

    const roundedValue = roundToNearestCent(rawValue);
    form.setValue("savingsGoal", roundedValue.toString());
  };

  const handleAmountBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const rawValue = toNumber(event.target.value);
    if (Number.isNaN(rawValue)) {
      return;
    }

    const roundedValue = roundToNearestCent(rawValue);
    form.setValue("savingsAmount", roundedValue.toString());
  };

  const onSubmit = (data: FormData) => {
    const parsedSavingsGoal = toNumber(data.savingsGoal);
    const parsedSavingsAmount = toNumber(data.savingsAmount);
    setWorkerSavingsData({
      ...workerSavingsData,
      savingsGoal: parsedSavingsGoal,
      savingsReason: data.savingsReason ?? "",
      savingsAmount: parsedSavingsAmount,
    });

    if (!workerSavingsData.savingsOnboardingCompleted) {
      history.push(TabRouterPath.SAVINGS_CONFIGURATION_ALLOCATION);
    } else {
      history.goBack();
    }
  };

  const { errors } = form.formState;

  if (workerSavingsData.savingsOnboardingCompleted) {
    return <Redirect to={TabRouterPath.SAVINGS_BOOK_CALL} />;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormProvider {...form}>
          <PageWrapper variant="tertiary">
            <StickyScrollPageHeader
              title="Saving Goals"
              scrollContainerRef={scrollRef}
              fallbackGoBackPath={TabRouterPath.ACCOUNT}
              headerVariant="tertiary"
            />
            <PageContent ref={scrollRef}>
              <Stack
                spacing={4}
                justifyContent="space-between"
                height="100%"
                sx={{ ...getSafeAreaPaddings({ bottom: true }) }}
              >
                <Stack justifyContent="center" alignItems="center" pt={6} spacing={6} width="100%">
                  <Controller
                    name="savingsReason"
                    control={form.control}
                    render={({ field }) => (
                      <Stack spacing={2} width="100%">
                        <FormLabel id="savings-goal-reason">
                          <Text bold>What are you saving for?</Text>
                        </FormLabel>
                        <RadioGroup {...field}>
                          {SAVINGS_GOAL_OPTIONS.map((category) => (
                            <RadioField
                              key={category}
                              value={category}
                              label={SAVINGS_GOAL_SELECT_DISPLAY[category]}
                            />
                          ))}
                        </RadioGroup>
                      </Stack>
                    )}
                  />

                  <Controller
                    name="savingsGoal"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <Stack spacing={2} width="100%">
                        <Text bold>How much do you want to save?</Text>
                        <Input
                          {...field}
                          type="number"
                          inputProps={{
                            inputMode: "numeric",
                          }}
                          fullWidth
                          size="large"
                          unit="$"
                          placeholder="$10,000"
                          onBlur={handleGoalBlur}
                          onKeyDown={handleNumberInputKeyDown}
                        />
                        {fieldState.error && <Text color="error">{fieldState.error.message}</Text>}
                      </Stack>
                    )}
                  />

                  <Controller
                    name="savingsAmount"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <Stack spacing={2} width="100%">
                        <Text bold>How much have you already saved towards your goal?</Text>
                        <Input
                          {...field}
                          type="number"
                          inputProps={{
                            inputMode: "numeric",
                          }}
                          fullWidth
                          size="large"
                          unit="$"
                          placeholder="$0"
                          onBlur={handleAmountBlur}
                          onKeyDown={handleNumberInputKeyDown}
                        />
                        {fieldState.error && <Text color="error">{fieldState.error.message}</Text>}
                      </Stack>
                    )}
                  />
                </Stack>
                <Button disabled={!isEmpty(errors)} fullWidth type="submit">
                  {workerSavingsData.savingsOnboardingCompleted ? "Submit" : "Continue"}
                </Button>
              </Stack>
            </PageContent>
          </PageWrapper>
        </FormProvider>
      </form>

      <BottomSheet
        modalState={onboardingModalState}
        footer={<DialogFooter orientation="vertical" onClose={handleOnboardingClose} />}
      >
        <BottomSheetIllustrationContent
          illustrationType="pay"
          title="Welcome to Savings"
          description={
            <Stack spacing={3}>
              <Text>
                Clipboard wants to help you save more money, faster. Every time you work a shift, we
                set aside a portion of your earnings as savings. You can access this money anytime.
              </Text>
              <Text>Watch your savings grow as you work towards your goal!</Text>
            </Stack>
          }
        />
      </BottomSheet>
    </ThemeProvider>
  );
}
